import {
  type WrappedInsight,
  type WrappedInsightType,
} from "@clipboard-health/contract-backend-main";
import { BottomSheet, Text, Title, type UseModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, DialogContent, Stack } from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { BottomSheetHeader } from "../components/BottomSheetHeader";
import { Button } from "../components/Button";
import { DialogFooter } from "../components/DialogFooter";
import { KnownInsightContentContainer } from "./Insight/KnownInsightContentContainer";

interface ShareNudgeBottomSheetProps {
  modalState: UseModalState;
  knownInsights: WrappedInsight[];
  handleShare: (insight: WrappedInsightType) => Promise<void>;
}

export function ShareNudgeBottomSheet(props: ShareNudgeBottomSheetProps) {
  const { modalState, knownInsights, handleShare } = props;

  const history = useHistory();
  const targetPreviewsHeight = 0.4 * window.innerHeight;

  const [selectedInsight, setSelectedInsight] = useState<WrappedInsight | undefined>(
    knownInsights.at(-1)
  );

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Scroll to the last item when the bottom sheet opens
  useEffect(() => {
    if (!modalState.modalIsOpen) {
      return undefined;
    }

    // The bottom sheet has an animation, so we need to wait for it to complete
    // Try multiple times with increasing delays to ensure it works
    const delays = [100, 300, 500];
    const timeouts = delays.map((delay) =>
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollLeft = scrollContainerRef.current.scrollWidth;
        }
      }, delay)
    );

    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [modalState.modalIsOpen]);

  const handleClose = useCallback(() => {
    modalState.closeModal();
  }, [modalState]);

  return (
    <BottomSheet
      footer={
        <DialogFooter orientation="vertical">
          <Button
            fullWidth
            disabled={!selectedInsight}
            variant="contained"
            size="large"
            endIconType={selectedInsight ? "plane" : undefined}
            onClick={async () => {
              if (isDefined(selectedInsight)) {
                await handleShare(selectedInsight.attributes.insightType);
              }
            }}
          >
            {selectedInsight ? "Share!" : "Select screen to share"}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            size="large"
            onClick={() => {
              modalState.closeModal();
              history.push(`${RootPaths.APP_V2_HOME}`);
            }}
          >
            Finish
          </Button>
        </DialogFooter>
      }
      header={<BottomSheetHeader onClose={handleClose} />}
      modalState={modalState}
      paperSx={{
        overflow: "hidden",
      }}
    >
      <DialogContent>
        <Stack
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "stretch",
            textAlign: "center",
            paddingTop: 8,
            gap: 6,
          }}
        >
          <Title semibold variant="h3" component="h3" sx={{ textWrap: "balance" }}>
            We are so proud of you!
          </Title>

          <Text semibold variant="body1">
            Tap any of your insights below to share them with your friends and family!
          </Text>

          <Box
            ref={scrollContainerRef}
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 3,
              overflowX: "auto",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
              mx: -7,
              px: 7,
            }}
          >
            {knownInsights.map((insight) => {
              const scaleByHeight = targetPreviewsHeight / window.innerHeight;
              const scaledWidth = window.innerWidth * scaleByHeight;

              return (
                <Box
                  key={insight.id}
                  sx={(theme) => ({
                    position: "relative",
                    height: `${targetPreviewsHeight}px`,
                    width: `${scaledWidth}px`, // Width determined by scale
                    overflow: "hidden",
                    flexShrink: 0, // Prevent shrinking in scroll view
                    borderRadius: theme.borderRadius?.small,
                    boxSizing: "border-box",
                    // Reset text-align to prevent inheriting from parent
                    textAlign: "left",
                    border:
                      selectedInsight?.id === insight.id
                        ? `${theme.borderWidth.thick} solid ${theme.palette.primary.main}`
                        : "none",
                  })}
                >
                  <Box
                    sx={{
                      transform: `scale(${scaleByHeight})`,
                      transformOrigin: "top left",
                      width: "100vw", // Original full width
                      height: "100vh", // Original full height
                    }}
                    onClick={() => {
                      setSelectedInsight(insight);
                    }}
                  >
                    <KnownInsightContentContainer visible isPreview insight={insight} />
                  </Box>
                </Box>
              );
            })}
          </Box>

          <Text variant="body1">
            Thanks for being a valued part of the Clipboard Health community!
          </Text>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
